import { View, Text, StyleSheet } from "@react-pdf/renderer";
import type { ResultSegment } from "../../types";
import { getScoreColor, getScoreLabel, formatPercentage } from "../../utils/data-mapper";

const styles = StyleSheet.create({
  container: {
    marginVertical: 10,
    paddingVertical: 10,
    borderBottomWidth: 1,
    borderBottomColor: "#e5e7eb"
  },
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 8
  },
  title: {
    fontSize: 12,
    fontWeight: "bold",
    color: "#1f2937"
  },
  scoreBadge: {
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: 3,
    paddingHorizontal: 8,
    borderRadius: 4
  },
  scoreText: {
    fontSize: 10,
    color: "#ffffff",
    fontWeight: "bold"
  },
  description: {
    fontSize: 9,
    color: "#6b7280",
    marginBottom: 8,
    lineHeight: 1.4
  },
  feedbacksContainer: {
    marginTop: 8
  },
  feedbacksTitle: {
    fontSize: 10,
    fontWeight: "bold",
    color: "#374151",
    marginBottom: 5
  },
  feedbackItem: {
    flexDirection: "row",
    marginBottom: 4,
    paddingLeft: 10
  },
  bullet: {
    width: 4,
    height: 4,
    borderRadius: 2,
    backgroundColor: "#9ca3af",
    marginRight: 6,
    marginTop: 4
  },
  feedbackText: {
    flex: 1,
    fontSize: 9,
    color: "#4b5563",
    lineHeight: 1.3
  },
  metricsRow: {
    flexDirection: "row",
    marginTop: 8,
    gap: 20
  },
  metricItem: {
    flexDirection: "row",
    alignItems: "center"
  },
  metricLabel: {
    fontSize: 8,
    color: "#6b7280",
    marginRight: 4
  },
  metricValue: {
    fontSize: 9,
    fontWeight: "bold"
  }
});

interface FeedbackItem {
  title: string;
  description: string;
}

interface GroupAnalysisProperties {
  grupoNome: string;
  grupoDescricao?: string;
  resultado: ResultSegment;
  feedbacks?: FeedbackItem[];
}

/**
 * GroupAnalysis component for displaying detailed analysis of a dimension/group
 * @param root0
 * @param root0.grupoNome
 * @param root0.grupoDescricao
 * @param root0.resultado
 * @param root0.feedbacks
 */
export function GroupAnalysis({ grupoNome, grupoDescricao, resultado, feedbacks = [] }: GroupAnalysisProperties) {
  const scoreColor = getScoreColor(resultado.value);
  const scoreLabel = getScoreLabel(resultado.value);

  return (
    <View style={styles.container} wrap={false}>
      {/* Header with title and score badge */}
      <View style={styles.header}>
        <Text style={styles.title}>{grupoNome}</Text>
        <View style={[styles.scoreBadge, { backgroundColor: scoreColor }]}>
          <Text style={styles.scoreText}>
            {formatPercentage(resultado.value)} - {scoreLabel}
          </Text>
        </View>
      </View>

      {/* Description */}
      {grupoDescricao && <Text style={styles.description}>{grupoDescricao}</Text>}

      {/* Metrics */}
      <View style={styles.metricsRow}>
        <View style={styles.metricItem}>
          <Text style={styles.metricLabel}>Pontuação:</Text>
          <Text style={[styles.metricValue, { color: scoreColor }]}>{formatPercentage(resultado.value)}</Text>
        </View>

        {resultado.autoAvaliacao !== undefined && (
          <View style={styles.metricItem}>
            <Text style={styles.metricLabel}>Autoavaliação:</Text>
            <Text style={styles.metricValue}>{formatPercentage(resultado.autoAvaliacao)}</Text>
          </View>
        )}

        {resultado.autoAvaliacao !== undefined && (
          <View style={styles.metricItem}>
            <Text style={styles.metricLabel}>Diferença:</Text>
            <Text
              style={[styles.metricValue, { color: resultado.value > resultado.autoAvaliacao ? "#22c55e" : "#ef4444" }]}
            >
              {resultado.value > resultado.autoAvaliacao ? "+" : ""}
              {formatPercentage(resultado.value - resultado.autoAvaliacao)}
            </Text>
          </View>
        )}
      </View>

      {/* Feedbacks */}
      {feedbacks.length > 0 && (
        <View style={styles.feedbacksContainer}>
          <Text style={styles.feedbacksTitle}>Pontos de atenção:</Text>
          {feedbacks.map((feedback, index) => (
            <View key={index} style={styles.feedbackItem}>
              <View style={styles.bullet} />
              <Text style={styles.feedbackText}>
                <Text style={{ fontWeight: "bold" }}>{feedback.title}: </Text>
                {feedback.description}
              </Text>
            </View>
          ))}
        </View>
      )}
    </View>
  );
}
